<template>
  <div class="od-page">
    <div class="od-body">
      <!-- 数据来源条 -->
      <div class="od-toolbar">
        <div class="od-source">
          <span class="od-source-item">
            <span class="od-dot" :class="sync.running ? 'od-dot-run' : 'od-dot-ok'"></span>
            数据截至
            <el-select v-model="snapshotDate" size="small" class="od-snap-select" filterable @change="reloadAll">
              <el-option v-for="d in snapshots" :key="d" :label="d" :value="d" />
            </el-select>
          </span>
          <span v-if="snapshotMeta && snapshotMeta.has_soa" class="od-source-item od-muted">SOA：{{ snapshotMeta.soa_filename }}（{{ snapshotMeta.soa_uploaded_at }} 上传）</span>
          <span v-if="sync.running" class="od-source-item od-run-text">正在同步 AWS / SOA…</span>
          <span v-else-if="sync.error" class="od-source-item od-err-text">上次同步失败：{{ sync.error }}</span>
          <span v-else-if="sync.finished_at" class="od-source-item od-muted">上次同步 {{ sync.finished_at }} · {{ sync.message }}</span>
        </div>
        <div class="od-spacer"></div>
        <template v-if="isAdmin">
          <button class="od-btn" @click="soaDialog = true">上传 SOA</button>
          <button class="od-btn" :disabled="sync.running" @click="triggerSync">同步 AWS</button>
        </template>
        <el-dropdown trigger="click" :disabled="exporting" @command="exportExcel">
          <button class="od-btn" :disabled="exporting">{{ exporting ? '导出中…' : '导出 Excel ▾' }}</button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="system">销售按系统归属</el-dropdown-item>
              <el-dropdown-item command="soa" :disabled="!(snapshotMeta && snapshotMeta.has_soa)">销售按 SOA{{ snapshotMeta && snapshotMeta.has_soa ? '' : '（本快照无 SOA）' }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <button v-if="canRemind" class="od-btn od-btn-dark" @click="openRemind">发送催款邮件</button>
      </div>

      <div v-if="!empty && snapshotMeta && !snapshotMeta.has_soa" class="od-notice">
        <b>本快照未合并 SOA</b>：只按 AWS 口径计算逾期，无法识别「客户已付·AWS未到账」，也无法与 SOA 交叉核对金额和销售归属。
        <span v-if="isAdmin" class="od-notice-link" @click="soaDialog = true">上传 SOA 后自动重新合并</span>
      </div>
      <div v-else-if="!empty && soaStale" class="od-notice">
        本快照使用的 SOA 是 <b>{{ snapshotMeta.soa_age_days }} 天前</b>上传的，客户付款状态和销售归属可能已过时。
        <span v-if="isAdmin" class="od-notice-link" @click="soaDialog = true">上传最新 SOA</span>
      </div>

      <div v-if="empty" class="od-card od-empty-state">
        <div class="od-empty-title">还没有逾期数据</div>
        <div class="od-muted">{{ isAdmin ? '先上传内部 SOA 或点击「同步 AWS」生成第一份快照。' : '管理员同步后即可查看。' }}</div>
      </div>

      <template v-else>
        <!-- KPI -->
        <div class="od-kpi-row" v-loading="overviewLoading">
          <div v-for="k in kpiList" :key="k.label" class="od-kpi-card">
            <div class="od-kpi-label">{{ k.label }}</div>
            <div class="od-kpi-value" :style="{ color: k.color }">{{ k.value }}<span v-if="k.unit" class="od-kpi-unit">{{ k.unit }}</span></div>
            <div class="od-kpi-sub" :class="k.subClass">{{ k.sub }}</div>
          </div>
        </div>

        <!-- 分布 -->
        <div class="od-grid-3">
          <div class="od-card">
            <div class="od-card-header"><h3 class="od-card-title">账龄分布</h3><span class="od-card-hint">USD · 不含客户已付</span></div>
            <div class="od-aging">
              <div v-for="(a, i) in aging" :key="a.name" class="od-aging-row">
                <div class="od-aging-head">
                  <span><span class="od-legend-dot" :style="{ background: AGING_COLORS[i] }"></span>{{ a.name }} · {{ a.count }} 张</span>
                  <span class="od-num">${{ fmt(a.amount) }}</span>
                </div>
                <div class="od-bar"><div class="od-bar-fill" :style="{ width: barPct(a.amount, agingMax) + '%', background: AGING_COLORS[i] }"></div></div>
              </div>
            </div>
          </div>
          <div class="od-card">
            <div class="od-card-header"><h3 class="od-card-title">按账单周期分布</h3><span class="od-card-hint">未付金额 USD · 点击柱子只看当月，再点取消</span></div>
            <div ref="periodChart" class="od-chart"></div>
          </div>
          <div class="od-card">
            <div class="od-card-header">
              <h3 class="od-card-title">逾期金额 TOP 5</h3>
              <div class="od-seg">
                <span v-for="d in topDims" :key="d.value" class="od-seg-item" :class="{ active: topDim === d.value }" @click="changeTopDim(d.value)">{{ d.label }}</span>
              </div>
            </div>
            <div class="od-top">
              <div v-for="(t, i) in top" :key="t.name" class="od-top-row">
                <span class="od-top-rank" :class="{ hot: i < 3 }">{{ i + 1 }}</span>
                <span class="od-top-name" :title="t.name">{{ t.name }}</span>
                <span class="od-top-meta">{{ t.count }} 张 · {{ t.max_days }} 天</span>
                <span class="od-num od-top-amt">${{ fmt(t.amount) }}</span>
              </div>
              <div v-if="!top.length" class="od-empty">暂无数据</div>
            </div>
          </div>
        </div>

        <!-- 明细 -->
        <div class="od-card">
          <div class="od-card-header od-card-header-wrap">
            <h3 class="od-card-title">逾期发票明细</h3>
            <div class="od-seg od-status-seg">
              <span v-for="s in statusTabs" :key="s.value" class="od-seg-item" :class="{ active: filters.status === s.value }" @click="changeStatus(s.value)">
                {{ s.label }} <b>{{ statusCount[s.value || 'all'] || 0 }}</b>
              </span>
            </div>
          </div>
          <div class="od-filters">
            <div class="od-filter"><label>账单周期</label>
              <div class="od-range">
                <el-select v-model="filters.period_from" size="small" filterable clearable placeholder="起" @change="onPeriodChange('from')"><el-option v-for="p in periodsAsc" :key="p" :label="fmtPeriod(p)" :value="p" /></el-select>
                <span class="od-muted">—</span>
                <el-select v-model="filters.period_to" size="small" filterable clearable placeholder="止" @change="onPeriodChange('to')"><el-option v-for="p in periodsAsc" :key="p" :label="fmtPeriod(p)" :value="p" /></el-select>
              </div>
            </div>
            <div class="od-filter"><label>逾期等级</label>
              <el-select v-model="filters.level" size="small" multiple collapse-tags filterable clearable placeholder="全部" @change="search"><el-option v-for="l in LEVELS" :key="l" :label="l" :value="l" /></el-select>
            </div>
            <div v-if="userType !== 2" class="od-filter"><label>代理商</label>
              <el-select v-model="filters.agent_id" size="small" filterable clearable placeholder="全部" @change="search"><el-option v-for="a in options.agents" :key="a.value" :label="a.label" :value="a.value" /></el-select>
            </div>
            <div v-if="isAdmin" class="od-filter"><label>销售</label>
              <el-select v-model="filters.sales" size="small" filterable clearable placeholder="全部" @change="search"><el-option v-for="s in options.sales" :key="s" :label="s" :value="s" /></el-select>
            </div>
            <div class="od-filter"><label>类型</label>
              <el-select v-model="filters.invoice_type" size="small" filterable clearable placeholder="全部" @change="search"><el-option v-for="t in options.types" :key="t" :label="TYPE_LABEL[t] || t" :value="t" /></el-select>
            </div>
            <div v-if="options.currencies.length > 1" class="od-filter"><label>币种</label>
              <el-select v-model="filters.currency" size="small" filterable clearable placeholder="全部" @change="search"><el-option v-for="c in options.currencies" :key="c" :label="c" :value="c" /></el-select>
            </div>
            <div v-if="isAdmin" class="od-filter"><label>来源</label>
              <el-select v-model="filters.source" size="small" filterable clearable placeholder="全部" @change="search"><el-option v-for="(v, k) in SOURCE_LABEL" :key="k" :label="v" :value="k" /></el-select>
            </div>
            <div class="od-filter-actions">
              <button class="od-btn" @click="reset">重置</button>
            </div>
          </div>

          <el-table ref="table" :data="rows" v-loading="listLoading" row-key="invoice_id" class="od-table"
                    @selection-change="sel => (selection = sel)" @sort-change="onSort" @expand-change="onExpand">
            <el-table-column type="selection" width="40" :selectable="r => r.status !== 'paid_pending'" />
            <el-table-column type="expand" width="32">
              <template #default="{ row }">
                <div class="od-expand" v-loading="linkLoading[row.invoice_id]">
                  <div class="od-expand-meta">
                    <span v-if="row.soa_remark">SOA：{{ row.soa_remark }}</span>
                    <span v-if="row.check_note" class="od-warn-text">核对：{{ row.check_note }}</span>
                  </div>
                  <table class="od-mini-table">
                    <thead><tr><th>Link ID</th><th>账户名称</th><th>代理商</th><th>销售</th><th class="r">发票金额</th><th class="r">未付（按比例）</th></tr></thead>
                    <tbody>
                      <tr v-for="l in (links[row.invoice_id] || [])" :key="l.link_id">
                        <td class="od-mono">{{ l.link_id }}</td><td>{{ l.account_name || '—' }}</td><td>{{ l.agent_name || l.agent_id || '—' }}</td>
                        <td>{{ l.sales || '—' }}</td><td class="r od-num">{{ l.amount == null ? '—' : fmt(l.amount) }}</td>
                        <td class="r od-num">{{ l.unpaid_amount == null ? '—' : fmt(l.unpaid_amount) }}</td>
                      </tr>
                      <tr v-if="links[row.invoice_id] && !links[row.invoice_id].length"><td colspan="6" class="od-muted">平台无此发票的账户拆分（滞纳金发票只挂在 payer 下）</td></tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="invoice_id" label="Invoice ID" min-width="110"><template #default="{ row }"><span class="od-mono">{{ row.invoice_id }}</span></template></el-table-column>
            <el-table-column prop="payer_id" label="Payer ID" min-width="120"><template #default="{ row }"><span class="od-mono">{{ row.payer_id }}</span></template></el-table-column>
            <el-table-column prop="customer_name" label="客户" min-width="150" show-overflow-tooltip />
            <el-table-column v-if="userType !== 2" prop="agent_names" label="代理商" min-width="160" show-overflow-tooltip />
            <el-table-column v-if="isAdmin" prop="sales" label="销售" min-width="90">
              <template #default="{ row }">
                <div>{{ row.sales || '—' }}</div>
                <div v-if="salesDiffers(row)" class="od-warn-text od-small od-nowrap" :title="'SOA 归属：' + row.soa_sales">SOA：{{ row.soa_sales }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="billing_period" label="账单周期" min-width="84" sortable="custom"><template #default="{ row }">{{ fmtPeriod(row.billing_period) }}</template></el-table-column>
            <el-table-column prop="invoice_type" label="类型" min-width="96"><template #default="{ row }">{{ TYPE_LABEL[row.invoice_type] || row.invoice_type }}</template></el-table-column>
            <el-table-column prop="invoice_amount" label="发票金额" min-width="110" align="right" sortable="custom"><template #default="{ row }"><span class="od-num">{{ fmt(row.invoice_amount) }}</span></template></el-table-column>
            <el-table-column prop="unpaid_amount" label="未付金额" min-width="120" align="right" sortable="custom">
              <template #default="{ row }"><span class="od-num od-unpaid">{{ row.currency !== 'USD' ? row.currency + ' ' : '' }}{{ fmt(row.unpaid_amount) }}</span></template>
            </el-table-column>
            <el-table-column prop="due_date" label="到期日" min-width="96" sortable="custom" />
            <el-table-column prop="aging_days" label="逾期天数" min-width="90" align="right" sortable="custom"><template #default="{ row }"><span class="od-num">{{ row.aging_days }}</span></template></el-table-column>
            <el-table-column prop="level" label="等级" min-width="64"><template #default="{ row }"><span class="od-tag" :class="'lv-' + levelKey(row.level)">{{ row.level }}</span></template></el-table-column>
            <el-table-column prop="status" label="状态" min-width="130">
              <template #default="{ row }">
                <span class="od-tag" :class="'st-' + row.status">{{ STATUS_LABEL[row.status] }}</span>
                <el-tooltip v-if="row.check_note" :content="row.check_note" placement="top"><span class="od-diff-dot">!</span></el-tooltip>
              </template>
            </el-table-column>
            <el-table-column v-if="isAdmin" prop="source" label="来源" min-width="90"><template #default="{ row }"><span class="od-muted od-nowrap">{{ SOURCE_LABEL[row.source] }}</span></template></el-table-column>
          </el-table>
          <div class="od-table-foot">
            <span class="od-muted">共 {{ total }} 张，未付合计 ${{ fmt(listSum) }}<template v-if="selection.length">，已选 {{ selection.length }} 张</template></span>
            <el-pagination background layout="sizes, prev, pager, next" :total="total" :page-sizes="[20, 50, 100]"
                           v-model:current-page="page" v-model:page-size="limit" @current-change="loadList" @size-change="search" />
          </div>
        </div>

        <!-- payer 对账（管理员） -->
        <div v-if="isAdmin" class="od-card">
          <div class="od-card-header od-clickable" @click="togglePayers">
            <h3 class="od-card-title">Payer 对账：AWS vs SOA</h3>
            <span class="od-card-hint">差额按发票拆原因 · {{ payersOpen ? '收起' : '展开' }}</span>
          </div>
          <el-table v-if="payersOpen" :data="payers" v-loading="payerLoading" class="od-table" max-height="420">
            <el-table-column prop="payer_id" label="Payer ID" min-width="120"><template #default="{ row }"><span class="od-mono">{{ row.payer_id }}</span></template></el-table-column>
            <el-table-column prop="payer_name" label="名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="aws_past_due" label="AWS 逾期" min-width="110" align="right"><template #default="{ row }"><span class="od-num">{{ fmt(row.aws_past_due) }}</span></template></el-table-column>
            <el-table-column prop="soa_past_due" label="SOA 逾期" min-width="110" align="right"><template #default="{ row }"><span class="od-num">{{ fmt(row.soa_past_due) }}</span></template></el-table-column>
            <el-table-column prop="diff" label="差额(AWS-SOA)" min-width="120" align="right">
              <template #default="{ row }"><span class="od-num" :class="{ 'od-warn-text': Math.abs(row.diff) > 0.01 }">{{ fmt(row.diff) }}</span></template>
            </el-table-column>
            <el-table-column prop="reason" label="差额说明" min-width="320">
              <template #default="{ row }"><span :class="row.reason ? '' : 'od-muted'">{{ row.reason || '一致' }}</span></template>
            </el-table-column>
          </el-table>
        </div>
      </template>
    </div>

    <!-- 上传 SOA -->
    <el-dialog v-model="soaDialog" title="上传内部 SOA" width="480px" class="od-dialog">
      <el-upload drag :auto-upload="false" :limit="1" accept=".xlsx,.xlsm" :on-change="f => (soaFile = f.raw)" :on-remove="() => (soaFile = null)">
        <div class="od-upload-text">拖入 SOA Excel 或 <b>点击选择</b></div>
        <div class="od-muted">读取「SOA」sheet，上传后自动与 AWS 结果重新合并</div>
      </el-upload>
      <el-checkbox v-model="soaRefreshAws" class="od-mt">同时重新拉取 AWS 数据（较慢）</el-checkbox>
      <template #footer>
        <button class="od-btn" @click="soaDialog = false">取消</button>
        <button class="od-btn od-btn-dark" :disabled="!soaFile || uploading" @click="uploadSoa">{{ uploading ? '上传中…' : '上传' }}</button>
      </template>
    </el-dialog>

    <!-- 催款邮件 -->
    <el-dialog v-model="remindDialog" title="发送催款邮件" width="860px" class="od-dialog">
      <div class="od-muted od-mb">
        {{ selection.length ? `已选 ${selection.length} 张发票` : '按当前筛选条件' }}，按代理商汇总，每家附各自的逾期 Excel。
        「客户已付·AWS未到账」的发票不发送；未归属代理商的发票无法发送。收件人、抄送即实际发送对象，可直接修改。
      </div>
      <el-table ref="remindTable" :data="remindAgents" v-loading="remindLoading" class="od-table" max-height="380" @selection-change="s => (remindSel = s)">
        <el-table-column type="selection" width="40" :selectable="r => r.sendable" />
        <el-table-column prop="agent_name" label="代理商" min-width="170">
          <template #default="{ row }">
            <div class="od-ellipsis" :title="row.agent_name">{{ row.agent_name }}</div>
            <div v-if="row.sales_no_email" class="od-warn-text od-small">销售 {{ row.sales_no_email }} 未登记邮箱</div>
          </template>
        </el-table-column>
        <el-table-column prop="count" label="发票" width="60" align="right" />
        <el-table-column label="未付(USD)" width="110" align="right"><template #default="{ row }"><span class="od-num">{{ fmt(row.amount) }}</span></template></el-table-column>
        <el-table-column label="收件人" min-width="200"><template #default="{ row }"><el-input v-model="row.to" size="small" :disabled="!row.sendable" placeholder="多个用 ; 分隔" /></template></el-table-column>
        <el-table-column label="抄送" min-width="170"><template #default="{ row }"><el-input v-model="row.cc" size="small" :disabled="!row.sendable" placeholder="无抄送，多个用 ; 分隔" /></template></el-table-column>
      </el-table>
      <el-input v-model="remindMessage" type="textarea" :rows="2" class="od-mt" placeholder="补充说明（可选，会显示在邮件正文）" maxlength="300" show-word-limit />
      <template #footer>
        <button class="od-btn" @click="remindDialog = false">取消</button>
        <button class="od-btn od-btn-dark" :disabled="!remindSel.length || sending" @click="sendRemind">{{ sending ? '发送中…' : `发送 ${remindSel.length} 家` }}</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { ajaxGet, ajaxPost, download } from '@/api/request'

const LEVELS = ['轻度', '中度', '重度', '严重']
const AGING_COLORS = ['#d4a14a', '#e07941', '#c05c5c', '#8f2d2d']
const STATUS_LABEL = { unpaid: '未付', partial: '部分付款', paid_pending: '客户已付·AWS未到账' }
const SOURCE_LABEL = { both: 'AWS+SOA', aws: 'AWS', soa: 'SOA' }
const TYPE_LABEL = { Cloud: 'Cloud', Marketplace: 'Marketplace', LateFee: '滞纳金', 'RI/SP': 'RI/SP' }
const emptyFilters = () => ({ period_from: '', period_to: '', level: [], agent_id: '', sales: '', invoice_type: '', currency: '', source: '', status: '' })

export default {
  name: 'overdueManage',
  data() {
    return {
      LEVELS, AGING_COLORS, STATUS_LABEL, SOURCE_LABEL, TYPE_LABEL,
      userType: Number(JSON.parse(localStorage.getItem('userInfo') || '{}').type) || 0,
      snapshotDate: '', snapshots: [], snapshotMeta: null, soa: null, sync: {}, empty: false,
      kpi: {}, aging: [], byPeriod: [], top: [], topDim: 'agent', statusCount: {},
      options: { agents: [], sales: [], periods: [], types: [], currencies: [] },
      filters: emptyFilters(), sort: 'unpaid', order: 'desc',
      rows: [], total: 0, listSum: 0, page: 1, limit: 20, selection: [],
      links: {}, linkLoading: {},
      overviewLoading: false, listLoading: false, exporting: false,
      payersOpen: false, payers: [], payerLoading: false,
      soaDialog: false, soaFile: null, soaRefreshAws: false, uploading: false,
      remindDialog: false, remindAgents: [], remindSel: [], remindMessage: '', remindLoading: false, sending: false,
      chart: null, pollTimer: null,
    }
  },
  computed: {
    isAdmin() { return this.userType === 1 },
    canRemind() { return this.userType === 1 || this.userType === 3 },
    soaStale() {
      const m = this.snapshotMeta
      return !!(m && m.has_soa && m.soa_age_days != null && m.soa_age_days > m.soa_stale_days)
    },
    topDims() {
      const d = [{ value: 'agent', label: '代理商' }, { value: 'customer', label: '客户' }]
      return this.isAdmin ? d.concat({ value: 'sales', label: '销售' }) : d
    },
    statusTabs() {
      const t = [{ value: '', label: '全部' }, { value: 'unpaid', label: '未付' }, { value: 'partial', label: '部分付款' }, { value: 'paid_pending', label: '客户已付·AWS未到账' }]
      return this.isAdmin ? t.concat({ value: 'diff', label: '核对差异' }) : t
    },
    agingMax() { return Math.max(1, ...this.aging.map(a => a.amount)) },
    periodsAsc() { return [...(this.options.periods || [])].sort() },
    kpiList() {
      const k = this.kpi || {}
      const prev = k.prev
      const delta = (cur, old, money) => {
        if (old == null) return { sub: '首个快照', subClass: '' }
        const d = cur - old
        if (Math.abs(d) < 0.005) return { sub: `较 ${prev.snapshot_date} 持平`, subClass: '' }
        return { sub: `较 ${prev.snapshot_date} ${d > 0 ? '+' : '-'}${money ? '$' + this.fmt(Math.abs(d)) : Math.abs(d)}`, subClass: d > 0 ? 'od-up' : 'od-down' }
      }
      const other = (k.other_currency || []).map(o => `${o.currency} ${this.fmt(o.amount)}`).join('，')
      const longest = k.longest || {}
      return [
        { label: '逾期未付（USD）', value: '$' + this.fmt(k.amount_usd, 0), color: '#c05c5c', ...delta(k.amount_usd || 0, prev && prev.amount_usd, true), ...(other ? { sub: '另 ' + other } : {}) },
        { label: '逾期发票', value: k.count || 0, unit: '张', color: '#1b1b1f', ...delta(k.count || 0, prev && prev.count, false) },
        { label: '涉及代理商 / 客户', value: `${k.agents || 0} / ${k.customers || 0}`, color: '#1b1b1f', sub: `${k.payers || 0} 个 payer` },
        { label: '最长逾期', value: longest.aging_days || 0, unit: '天', color: (longest.aging_days || 0) > 90 ? '#8f2d2d' : '#e07941', sub: longest.customer_name || longest.invoice_id || '—' },
        { label: '客户已付·AWS未到账', value: '$' + this.fmt((k.paid_pending || {}).amount, 0), color: '#3d9e7a',
          sub: this.snapshotMeta && !this.snapshotMeta.has_soa ? '需上传 SOA 才能识别' : `${(k.paid_pending || {}).count || 0} 张，待 AWS 冲账` },
      ]
    },
  },
  mounted() {
    this.reloadAll()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeChart)
    clearInterval(this.pollTimer)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    fmt(v, digits = 2) {
      if (v == null || v === '') return '—'
      return Number(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    },
    fmtPeriod(p) { return p && p.length === 6 ? `${p.slice(0, 4)}-${p.slice(4)}` : (p || '—') },
    barPct(v, max) { return Math.max(2, Math.round((v / max) * 100)) },
    levelKey(l) { return { 轻度: 1, 中度: 2, 重度: 3, 严重: 4 }[l] || 0 },
    salesDiffers(r) {
      if (!r.soa_sales) return false
      const sys = (r.sales || '').split('、').filter(Boolean)
      return !r.soa_sales.split('、').some(s => sys.includes(s))
    },
    query(extra = {}) {
      const f = { ...this.filters, level: this.filters.level.join(',') }
      Object.keys(f).forEach(key => { if (f[key] === '' || f[key] == null) delete f[key] })
      return { ...f, snapshot_date: this.snapshotDate || undefined, ...extra }
    },
    async reloadAll() {
      await this.loadOverview()
      if (!this.empty) {
        this.page = 1
        this.loadList()
        if (this.payersOpen) this.loadPayers()
      }
    },
    async loadOverview() {
      this.overviewLoading = true
      try {
        const res = await ajaxGet({ url: '/bill/overdue/overview/', params: this.query({ top_dim: this.topDim, status: undefined }) })
        if (!res || res.code !== 2000) { this.$message.error((res && res.msg) || '加载失败'); return }
        const d = res.data
        this.soa = d.soa
        this.snapshotMeta = d.snapshot_meta || null
        this.sync = d.sync || {}
        this.empty = !!d.empty
        if (this.sync.running) this.startPoll()
        if (this.empty) return
        this.snapshotDate = d.snapshot_date
        this.snapshots = d.snapshots || []
        this.kpi = d.kpi
        this.aging = d.aging
        this.byPeriod = d.by_period
        this.top = d.top
        this.statusCount = d.status_count || {}
        this.options = d.options
        this.$nextTick(this.renderChart)
      } finally {
        this.overviewLoading = false
      }
    },
    async loadList() {
      this.listLoading = true
      try {
        const res = await ajaxGet({ url: '/bill/overdue/list/', params: this.query({ page: this.page, limit: this.limit, sort: this.sort, order: this.order }) })
        if (res && res.code === 2000) {
          this.rows = res.data.rows
          this.total = res.data.total
          this.listSum = res.data.sum
        }
      } finally {
        this.listLoading = false
      }
    },
    search() { this.page = 1; this.loadOverview(); this.loadList() },
    pickMonth(p) {
      const same = this.filters.period_from === p && this.filters.period_to === p
      this.filters.period_from = same ? '' : p
      this.filters.period_to = same ? '' : p
      this.search()
    },
    onPeriodChange(side) {
      const f = this.filters
      if (f.period_from && f.period_to && f.period_from > f.period_to) {
        if (side === 'from') f.period_to = f.period_from
        else f.period_from = f.period_to
      }
      this.search()
    },
    inPeriod(p) {
      const { period_from: a, period_to: b } = this.filters
      return (!a || p >= a) && (!b || p <= b)
    },
    reset() { this.filters = emptyFilters(); this.search() },
    changeStatus(v) { this.filters.status = v; this.page = 1; this.loadList() },
    async changeTopDim(v) {
      this.topDim = v
      const res = await ajaxGet({ url: '/bill/overdue/overview/', params: this.query({ top_dim: v, status: undefined }) })
      if (res && res.code === 2000) this.top = res.data.top
    },
    onSort({ prop, order }) {
      const map = { unpaid_amount: 'unpaid', aging_days: 'aging', due_date: 'due', billing_period: 'period', invoice_amount: 'amount' }
      this.sort = order ? map[prop] : 'unpaid'
      this.order = order === 'ascending' ? 'asc' : 'desc'
      this.page = 1
      this.loadList()
    },
    async onExpand(row, expanded) {
      if (!expanded.some(r => r.invoice_id === row.invoice_id) || this.links[row.invoice_id]) return
      this.linkLoading = { ...this.linkLoading, [row.invoice_id]: true }
      try {
        const res = await ajaxGet({ url: '/bill/overdue/links/', params: { invoice_id: row.invoice_id, snapshot_date: this.snapshotDate } })
        this.links = { ...this.links, [row.invoice_id]: (res && res.data) || [] }
      } finally {
        this.linkLoading = { ...this.linkLoading, [row.invoice_id]: false }
      }
    },
    renderChart() {
      const el = this.$refs.periodChart
      if (!el) return
      if (!this.chart) {
        this.chart = echarts.init(el)
        this.chart.on('click', p => { const d = (this.byPeriod || [])[p.dataIndex]; if (d) this.pickMonth(d.period) })
      }
      const data = this.byPeriod || []
      const sel = this.filters.period_from || this.filters.period_to
      const max = Math.max(0, ...data.map(d => d.amount || 0))
      const colorOf = d => sel ? (this.inPeriod(d.period) ? '#c05c5c' : '#e4e4e7') : (d.amount === max ? '#c05c5c' : '#cbd5e1')
      this.chart.setOption({
        grid: { left: 8, right: 8, top: 24, bottom: 4, containLabel: true },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: p => `${p[0].axisValue}<br/>$${this.fmt(p[0].value)} · ${data[p[0].dataIndex].count} 张` },
        xAxis: { type: 'category', data: data.map(d => this.fmtPeriod(d.period)), axisTick: { show: false }, axisLine: { lineStyle: { color: '#e4e4e7' } }, axisLabel: { color: '#71717a', fontSize: 11 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f2f4' } }, axisLabel: { color: '#71717a', fontSize: 11, formatter: v => v >= 1000 ? (v / 1000) + 'k' : v } },
        series: [{
          type: 'bar', barMaxWidth: 34, data: data.map(d => ({ value: d.amount, itemStyle: { color: colorOf(d), borderRadius: [4, 4, 0, 0] } })),
          label: { show: true, position: 'top', color: '#52525b', fontSize: 10, formatter: p => p.value >= 1000 ? (p.value / 1000).toFixed(1) + 'k' : p.value },
        }],
      }, true)
    },
    resizeChart() { if (this.chart) this.chart.resize() },
    togglePayers() {
      this.payersOpen = !this.payersOpen
      if (this.payersOpen && !this.payers.length) this.loadPayers()
    },
    async loadPayers() {
      this.payerLoading = true
      try {
        const res = await ajaxGet({ url: '/bill/overdue/payers/', params: { snapshot_date: this.snapshotDate } })
        this.payers = (res && res.data) || []
      } finally {
        this.payerLoading = false
      }
    },
    async exportExcel(basis = 'system') {
      this.exporting = true
      try {
        const blob = await download('/bill/overdue/export/', this.query({ sales_basis: basis }))
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `逾期发票_${this.snapshotDate}_销售按${basis === 'soa' ? 'SOA' : '系统'}.xlsx`
        a.click()
        URL.revokeObjectURL(a.href)
      } finally {
        this.exporting = false
      }
    },
    async triggerSync() {
      try {
        await this.$confirm('从 AWS 拉取所有 global payer 的逾期总额并反推发票，约需几分钟，期间页面数据不变。', '同步 AWS 逾期数据', { confirmButtonText: '开始同步', cancelButtonText: '取消' })
      } catch (e) { return }
      const res = await ajaxPost({ url: '/bill/overdue/sync/', params: {} })
      if (res && res.code === 2000) {
        this.$message.success(res.msg)
        this.sync = res.data
        this.startPoll()
      } else {
        this.$message.warning((res && res.msg) || '同步失败')
      }
    },
    startPoll() {
      clearInterval(this.pollTimer)
      this.sync = { ...this.sync, running: true }
      this.pollTimer = setInterval(async () => {
        const res = await ajaxGet({ url: '/bill/overdue/sync/' })
        if (!res || !res.data) return
        this.sync = res.data
        if (!res.data.running) {
          clearInterval(this.pollTimer)
          this.snapshotDate = ''
          if (res.data.error) this.$message.error('同步失败：' + res.data.error)
          else this.$message.success('同步完成：' + res.data.message)
          this.reloadAll()
        }
      }, 5000)
    },
    async uploadSoa() {
      const fd = new FormData()
      fd.append('file', this.soaFile)
      fd.append('refresh_aws', this.soaRefreshAws ? '1' : '0')
      this.uploading = true
      try {
        const res = await ajaxPost({ url: '/bill/overdue/soa/', params: fd })
        if (res && res.code === 2000) {
          this.$message.success(`${res.msg}：共 ${res.data.rows} 行，其中逾期 ${res.data.past_due_rows} 行`)
          this.soaDialog = false
          this.soaFile = null
          if (res.data.sync_started) this.startPoll()
        } else {
          this.$message.error((res && res.msg) || '上传失败')
        }
      } finally {
        this.uploading = false
      }
    },
    async openRemind() {
      this.remindDialog = true
      this.remindLoading = true
      this.remindAgents = []
      try {
        const res = await ajaxPost({ url: '/bill/overdue/remind/', params: this.remindParams({ action: 'preview' }) })
        this.remindAgents = (res && res.data && res.data.agents) || []
        this.$nextTick(() => this.remindAgents.filter(a => a.sendable && a.to).forEach(a => this.$refs.remindTable.toggleRowSelection(a, true)))
      } finally {
        this.remindLoading = false
      }
    },
    remindParams(extra) {
      return { ...this.query(), invoice_ids: this.selection.map(r => r.invoice_id), ...extra }
    },
    async sendRemind() {
      try {
        await this.$confirm(`将给 ${this.remindSel.length} 家代理商发送逾期邮件（附 Excel），确认发送？`, '发送催款邮件', { confirmButtonText: '发送', cancelButtonText: '取消', type: 'warning' })
      } catch (e) { return }
      this.sending = true
      try {
        const res = await ajaxPost({ url: '/bill/overdue/remind/', params: this.remindParams({
          action: 'send', message: this.remindMessage,
          targets: this.remindSel.map(a => ({ agent_id: a.agent_id, to: a.to, cc: a.cc, message: this.remindMessage })),
        }) })
        if (res && res.code === 2000) {
          const failed = (res.data || []).filter(r => r.status !== 'sent')
          if (failed.length) this.$message.warning(`${res.msg}；失败：${failed.map(f => f.agent_name + '(' + f.error + ')').join('，')}`)
          else this.$message.success(res.msg)
          this.remindDialog = false
        } else {
          this.$message.error((res && res.msg) || '发送失败')
        }
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.od-page { background: #f0f2f5; min-height: 100%; font-family: -apple-system, 'SF Pro Text', 'PingFang SC', 'Microsoft YaHei', sans-serif; letter-spacing: -0.011em; color: #1b1b1f; }
.od-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.od-muted { color: #71717a; font-size: 12px; }
.od-num { font-variant-numeric: tabular-nums; }
.od-mono { font-family: 'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
.od-warn-text { color: #c2410c; }
.od-err-text { color: #c05c5c; font-size: 12px; }
.od-run-text { color: #2563eb; font-size: 12px; }
.od-mt { margin-top: 12px; }
.od-mb { margin-bottom: 12px; }

.od-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.od-source { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; font-size: 12px; color: #52525b; }
.od-source-item { display: inline-flex; align-items: center; gap: 6px; }
.od-snap-select { width: 128px; }
.od-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.od-dot-ok { background: #3d9e7a; }
.od-dot-run { background: #2563eb; animation: od-pulse 1.2s infinite; }
@keyframes od-pulse { 50% { opacity: .3; } }
.od-spacer { flex: 1; }
.od-notice { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 8px; padding: 10px 16px; font-size: 13px; }
.od-notice-link { color: #2563eb; cursor: pointer; margin-left: 8px; font-weight: 500; }
.od-btn { height: 32px; padding: 0 14px; border-radius: 8px; font-size: 13px; font-weight: 500; border: 1px solid #e4e4e7; background: #fff; color: #1b1b1f; cursor: pointer; transition: background .15s; }
.od-btn:hover:not(:disabled) { background: #f4f4f5; }
.od-btn:disabled { opacity: .5; cursor: not-allowed; }
.od-btn-dark { background: #232f3e; border-color: #232f3e; color: #fff; }
.od-btn-dark:hover:not(:disabled) { background: #1b1b1f; }

.od-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; overflow: hidden; }
.od-card-header { padding: 12px 20px; border-bottom: 1px solid #e4e4e7; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.od-card-header-wrap { flex-wrap: wrap; }
.od-card-title { font-size: 14px; font-weight: 600; color: #1b1b1f; margin: 0; }
.od-card-hint { font-size: 12px; color: #a1a1aa; }
.od-clickable { cursor: pointer; }
.od-empty { padding: 24px; text-align: center; font-size: 13px; color: #71717a; }
.od-empty-state { padding: 48px; text-align: center; }
.od-empty-title { font-size: 15px; font-weight: 600; margin-bottom: 6px; }

.od-kpi-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.od-kpi-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 14px 16px; min-width: 0; }
.od-kpi-label { font-size: 12px; color: #71717a; }
.od-kpi-value { font-size: 24px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.3; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.od-kpi-unit { font-size: 12px; font-weight: 500; color: #71717a; margin-left: 4px; }
.od-kpi-sub { font-size: 12px; color: #a1a1aa; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.od-up { color: #c05c5c; }
.od-down { color: #3d9e7a; }

.od-grid-3 { display: grid; grid-template-columns: 1fr 1.2fr 1fr; gap: 12px; }
.od-aging { padding: 14px 20px 18px; display: flex; flex-direction: column; gap: 14px; }
.od-aging-head { display: flex; justify-content: space-between; font-size: 12px; color: #52525b; margin-bottom: 6px; }
.od-legend-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.od-bar { height: 6px; background: #f1f2f4; border-radius: 3px; overflow: hidden; }
.od-bar-fill { height: 100%; border-radius: 3px; transition: width 1s ease-out; }
.od-chart { height: 196px; padding: 0 12px 8px; }
.od-top { padding: 6px 20px 10px; }
.od-top-row { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid rgba(229, 231, 235, .5); font-size: 13px; }
.od-top-row:last-child { border-bottom: none; }
.od-top-rank { width: 18px; height: 18px; border-radius: 50%; background: #f1f2f4; color: #71717a; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.od-top-rank.hot { background: rgba(192, 92, 92, .12); color: #c05c5c; }
.od-top-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.od-top-meta { font-size: 11px; color: #a1a1aa; white-space: nowrap; }
.od-top-amt { font-weight: 600; white-space: nowrap; }

.od-seg { display: inline-flex; background: #f4f4f5; border-radius: 8px; padding: 2px; }
.od-seg-item { padding: 4px 10px; font-size: 12px; color: #71717a; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.od-seg-item b { font-weight: 600; margin-left: 2px; }
.od-seg-item.active { background: #fff; color: #1b1b1f; font-weight: 600; box-shadow: 0 1px 2px rgba(0, 0, 0, .06); }

.od-filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #e4e4e7; }
.od-filter { display: flex; flex-direction: column; gap: 4px; }
.od-filter label { font-size: 11px; color: #52525b; text-transform: uppercase; }
.od-filter .el-input, .od-filter .el-select { width: 150px; }
.od-range { display: flex; align-items: center; gap: 4px; }
.od-range .el-select { width: 104px; }
.od-filter-actions { display: flex; gap: 8px; margin-left: auto; }
.od-filters :deep(.el-input__wrapper), .od-filters :deep(.el-select__wrapper) { background: #f4f4f5; box-shadow: none; border-radius: 8px; }
.od-filters :deep(.el-input__wrapper.is-focus), .od-filters :deep(.el-select__wrapper.is-focused) { background: #fff; box-shadow: 0 0 0 1px #2563eb inset; }

.od-table { width: 100%; }
.od-table :deep(th.el-table__cell) { background: rgba(244, 244, 245, .5); color: #71717a; font-size: 11px; font-weight: 600; letter-spacing: .03em; }
.od-table :deep(td.el-table__cell) { font-size: 13px; color: #1b1b1f; }
.od-unpaid { font-weight: 600; color: #c05c5c; }
.od-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.od-small { font-size: 11px; }
.od-nowrap { white-space: nowrap; }
.od-chart { cursor: pointer; }
.od-table-foot { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; }

.od-tag { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 500; line-height: 18px; white-space: nowrap; }
.lv-1 { background: rgba(212, 161, 74, .12); color: #a87a1f; }
.lv-2 { background: rgba(224, 121, 65, .12); color: #c2410c; }
.lv-3 { background: rgba(192, 92, 92, .12); color: #b04444; }
.lv-4 { background: #8f2d2d; color: #fff; }
.st-unpaid { background: #f4f4f5; color: #52525b; }
.st-partial { background: rgba(37, 99, 235, .08); color: #2563eb; }
.st-paid_pending { background: rgba(61, 158, 122, .12); color: #2f7d60; }
.od-diff-dot { display: inline-flex; width: 14px; height: 14px; margin-left: 4px; border-radius: 50%; background: #fdba74; color: #fff; font-size: 10px; font-weight: 700; align-items: center; justify-content: center; cursor: help; vertical-align: middle; }

.od-expand { padding: 8px 56px 14px; background: #fafafa; }
.od-expand-meta { display: flex; gap: 20px; font-size: 12px; color: #52525b; margin-bottom: 8px; }
.od-mini-table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; border: 1px solid #e4e4e7; border-radius: 6px; }
.od-mini-table th { text-align: left; font-weight: 500; color: #71717a; padding: 7px 10px; border-bottom: 1px solid #e4e4e7; background: rgba(244, 244, 245, .5); }
.od-mini-table td { padding: 7px 10px; border-bottom: 1px solid rgba(229, 231, 235, .5); }
.od-mini-table .r { text-align: right; }
.od-upload-text { font-size: 13px; color: #52525b; margin-bottom: 4px; }

@media (max-width: 1400px) {
  .od-kpi-row { grid-template-columns: repeat(3, 1fr); }
  .od-grid-3 { grid-template-columns: 1fr 1fr; }
}
</style>
